import React, {
  createContext,
  JSX,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
} from "firebase/auth";
import { auth } from "../firebase.ts";
import { User } from "../types.ts";

export interface AuthContextValue {
  user?: User;
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessTokenSilently: () => Promise<string>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const toUser = (firebaseUser: FirebaseUser): User => ({
  sub: firebaseUser.uid,
  email: firebaseUser.email || undefined,
  email_verified: firebaseUser.emailVerified,
  name: firebaseUser.displayName || undefined,
  picture: firebaseUser.photoURL || undefined,
});

const defaultAuthContext: AuthContextValue = {
  user: undefined,
  isLoading: false,
  isAuthenticated: false,
  getAccessTokenSilently: () => Promise.reject({ error: "login_required" }),
  signIn: async () => {
    throw new Error("AuthProvider is missing from the component tree");
  },
  signUp: async () => {
    throw new Error("AuthProvider is missing from the component tree");
  },
  logout: async () => {},
};

const AuthContext = createContext<AuthContextValue>(defaultAuthContext);

interface AuthProviderProps {
  children: JSX.Element;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setFirebaseUser(nextUser);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextValue = {
    user: firebaseUser ? toUser(firebaseUser) : undefined,
    isLoading,
    isAuthenticated: !!firebaseUser,
    getAccessTokenSilently: () => {
      if (!auth.currentUser) {
        return Promise.reject({ error: "login_required" });
      }

      return auth.currentUser.getIdToken();
    },
    signIn: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    signUp: async (email, password) => {
      await createUserWithEmailAndPassword(auth, email, password);
    },
    logout: async () => {
      await signOut(auth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => useContext(AuthContext);

export default AuthProvider;
